import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Radio,
  RadioGroup,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { usePlans, useUpdateSubscription } from '../../hooks/subscriptions';
import { useOrganizationLimits } from '../../hooks/subscriptions/useOrganizationLimits';
import { useSnackbar } from '../SnackbarProvider';
import { formatPrice, formatBillingInterval } from '../../utils/subscriptions';
import type {
  OrganizationSubscription,
  LimitsHandling,
  CustomLimitsDto,
} from '../../types/subscriptions';

interface UpgradePlanDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
  currentSubscription: OrganizationSubscription;
}

const formatLimit = (value: number | null | undefined): string => {
  if (value === null || value === undefined || value === -1) return 'Unlimited';
  return String(value);
};

export const UpgradePlanDialog = ({
  open,
  onClose,
  organizationId,
  organizationName,
  currentSubscription,
}: UpgradePlanDialogProps) => {
  const { showMessage } = useSnackbar();
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [limitsHandling, setLimitsHandling] = useState<LimitsHandling>('apply');
  const [customLimits, setCustomLimits] = useState<CustomLimitsDto>({});

  const { data: plans, isLoading: plansLoading } = usePlans();
  const { data: currentLimits } = useOrganizationLimits(organizationId);
  const updateMutation = useUpdateSubscription(organizationId);

  const currentPlan = currentSubscription.plan;
  const selectedPlan = plans?.find((p) => p.id === selectedPlanId);

  // Initialize custom limits with current values when selecting custom option
  useEffect(() => {
    if (limitsHandling === 'custom' && currentLimits) {
      setCustomLimits({
        includedStores: currentLimits.limits.includedStores,
        includedWarehouses: currentLimits.limits.includedWarehouses,
        includedStaff: currentLimits.limits.includedStaff,
        maxProducts: currentLimits.limits.maxProducts,
        maxClients: currentLimits.limits.maxClients,
        maxLocations: currentLimits.limits.maxLocations,
        salesRateLimit: currentLimits.limits.salesRateLimit,
        offlineTransactionLimit: currentLimits.limits.offlineTransactionLimit,
      });
    }
  }, [limitsHandling, currentLimits]);

  const handleUpdate = () => {
    if (!selectedPlanId) {
      showMessage('Please select a new plan', 'warning');
      return;
    }

    if (selectedPlanId === currentPlan?.id) {
      showMessage('Please select a different plan', 'warning');
      return;
    }

    updateMutation.mutate(
      {
        planId: selectedPlanId,
        limitsHandling,
        ...(limitsHandling === 'custom' && { customLimits }),
      },
      {
        onSuccess: () => {
          showMessage('Plan changed successfully', 'success');
          onClose();
          setSelectedPlanId('');
          setLimitsHandling('apply');
          setCustomLimits({});
        },
        onError: (error: any) => {
          showMessage(
            error.response?.data?.message || 'Failed to change plan',
            'error'
          );
        },
      }
    );
  };

  const handleClose = () => {
    if (!updateMutation.isPending) {
      onClose();
      setSelectedPlanId('');
      setLimitsHandling('apply');
      setCustomLimits({});
    }
  };

  const handleCustomLimitChange = (field: keyof CustomLimitsDto, value: string) => {
    const numValue = value === '' ? undefined : parseInt(value, 10);
    setCustomLimits((prev) => ({ ...prev, [field]: numValue }));
  };

  const isPriceIncrease =
    selectedPlan && currentPlan
      ? selectedPlan.basePrice > currentPlan.basePrice
      : false;

  const isPriceDecrease =
    selectedPlan && currentPlan
      ? selectedPlan.basePrice < currentPlan.basePrice
      : false;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Change Subscription Plan</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Change subscription plan for <strong>{organizationName}</strong>
          </Typography>

          {plansLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              {/* Current Plan Info */}
              <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Current Plan
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {currentPlan?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentPlan ? formatPrice(currentPlan.basePrice) : 'N/A'}{' '}
                  {currentPlan ? formatBillingInterval(currentPlan.billingInterval) : ''}
                </Typography>
              </Box>

              {/* New Plan Selection */}
              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel>Select New Plan</InputLabel>
                <Select
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  label="Select New Plan"
                >
                  {plans
                    ?.filter((plan) => plan.isActive && plan.id !== currentPlan?.id)
                    .map((plan) => (
                      <MenuItem key={plan.id} value={plan.id}>
                        {plan.name} - {formatPrice(plan.basePrice)}{' '}
                        {formatBillingInterval(plan.billingInterval)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {/* Plan Comparison */}
              {selectedPlan && currentPlan && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Plan Comparison
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto 1fr',
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    {/* Current Plan Column */}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Current
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          <strong>Price:</strong> {formatPrice(currentPlan.basePrice)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Users:</strong> {currentPlan.maxUsers || 'Unlimited'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Stores:</strong> {currentPlan.maxStores || 'Unlimited'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Warehouses:</strong>{' '}
                          {currentPlan.maxWarehouses || 'Unlimited'}
                        </Typography>
                      </Stack>
                    </Box>

                    {/* Arrow */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ArrowForwardIcon color="action" />
                    </Box>

                    {/* New Plan Column */}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        New Plan
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          <strong>Price:</strong> {formatPrice(selectedPlan.basePrice)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Users:</strong> {selectedPlan.maxUsers || 'Unlimited'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Stores:</strong> {selectedPlan.maxStores || 'Unlimited'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Warehouses:</strong>{' '}
                          {selectedPlan.maxWarehouses || 'Unlimited'}
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Resource Limits Handling */}
                  <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
                    <FormLabel component="legend">Resource Limits</FormLabel>
                    <RadioGroup
                      value={limitsHandling}
                      onChange={(e) =>
                        setLimitsHandling(e.target.value as LimitsHandling)
                      }
                    >
                      <FormControlLabel
                        value="keep"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2">Keep current limits</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Preserve existing organization resource limits
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="apply"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2">
                              Apply plan limits (Recommended)
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Update limits to match the new plan: Stores:{' '}
                              {formatLimit(selectedPlan.maxStores)}, Staff:{' '}
                              {formatLimit(selectedPlan.maxUsers)}
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="custom"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2">Custom limits</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Set specific limits for this organization
                            </Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>

                  {/* Current vs Plan Limits Comparison Table */}
                  {limitsHandling !== 'custom' && currentLimits && (
                    <TableContainer component={Paper} sx={{ mt: 2 }} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Limit</TableCell>
                            <TableCell align="right">Current</TableCell>
                            <TableCell align="right">New Plan</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>Stores</TableCell>
                            <TableCell align="right">
                              {formatLimit(currentLimits.limits.includedStores)}
                            </TableCell>
                            <TableCell align="right">
                              {formatLimit(selectedPlan.maxStores)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Warehouses</TableCell>
                            <TableCell align="right">
                              {formatLimit(currentLimits.limits.includedWarehouses)}
                            </TableCell>
                            <TableCell align="right">
                              {formatLimit(selectedPlan.maxWarehouses)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Staff</TableCell>
                            <TableCell align="right">
                              {formatLimit(currentLimits.limits.includedStaff)}
                            </TableCell>
                            <TableCell align="right">
                              {formatLimit(selectedPlan.maxUsers)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Products</TableCell>
                            <TableCell align="right">
                              {formatLimit(currentLimits.limits.maxProducts)}
                            </TableCell>
                            <TableCell align="right">
                              {selectedPlan.tier === 'free' ? '50' : 'Unlimited'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Clients</TableCell>
                            <TableCell align="right">
                              {formatLimit(currentLimits.limits.maxClients)}
                            </TableCell>
                            <TableCell align="right">
                              {selectedPlan.tier === 'free' ? '100' : 'Unlimited'}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* Custom Limits Form */}
                  {limitsHandling === 'custom' && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Set Custom Limits
                      </Typography>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Use -1 for unlimited
                      </Typography>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            label="Stores"
                            type="number"
                            size="small"
                            fullWidth
                            value={customLimits.includedStores ?? ''}
                            onChange={(e) =>
                              handleCustomLimitChange('includedStores', e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            label="Warehouses"
                            type="number"
                            size="small"
                            fullWidth
                            value={customLimits.includedWarehouses ?? ''}
                            onChange={(e) =>
                              handleCustomLimitChange('includedWarehouses', e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            label="Staff"
                            type="number"
                            size="small"
                            fullWidth
                            value={customLimits.includedStaff ?? ''}
                            onChange={(e) =>
                              handleCustomLimitChange('includedStaff', e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            label="Max Products"
                            type="number"
                            size="small"
                            fullWidth
                            value={customLimits.maxProducts ?? ''}
                            onChange={(e) =>
                              handleCustomLimitChange('maxProducts', e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            label="Max Clients"
                            type="number"
                            size="small"
                            fullWidth
                            value={customLimits.maxClients ?? ''}
                            onChange={(e) =>
                              handleCustomLimitChange('maxClients', e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            label="Max Locations"
                            type="number"
                            size="small"
                            fullWidth
                            value={customLimits.maxLocations ?? ''}
                            onChange={(e) =>
                              handleCustomLimitChange('maxLocations', e.target.value)
                            }
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* Price Change Notice */}
                  {isPriceIncrease && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      This is an upgrade. The price difference will be prorated for the
                      current billing period.
                    </Alert>
                  )}
                  {isPriceDecrease && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      This is a downgrade. The new pricing will take effect at the start
                      of the next billing period.
                    </Alert>
                  )}
                </Box>
              )}

              {plans?.filter((p) => p.isActive && p.id !== currentPlan?.id).length ===
                0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No other active plans available.
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={updateMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdate}
          variant="contained"
          disabled={!selectedPlanId || updateMutation.isPending}
        >
          {updateMutation.isPending ? 'Changing...' : 'Change Plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
